/**
 * @file: index.tsx
 *
 * `ChatTransport` — owns the session WebSocket connection lifecycle for the
 * surface it wraps. It opens the connection on mount and closes it on
 * unmount, and nudges the unread reconcile so a freshly-opened surface
 * shows a correct badge.
 *
 * It owns ONLY the connection — not the thread list, contacts, or any data.
 * That makes it the single thing both a full chat mini-app and a bare
 * floating conversation window wrap with:
 *
 *   <ChatTransport>
 *     <ChatThreadsProvider><ChatContactsProvider>…</…></…>
 *   </ChatTransport>
 *
 *   <ChatTransport>
 *     <ChatConversationView />
 *   </ChatTransport>
 *
 * AppState handling lives inside the WS client itself; this only signals
 * intent. (Disconnect-on-lose-focus will be added here later.)
 */

import { FC, ReactNode, memo, useEffect } from 'react';
import { useChatSession } from '../session/context';

export const ChatTransport: FC<{ children: ReactNode }> = memo(
  ({ children }) => {
    const { wsClient, me, refreshChatUnreadCount } = useChatSession();
    const myPlatformUserId = me.platformUserId;

    useEffect(() => {
      if (!myPlatformUserId) return;
      void wsClient.connect();
      refreshChatUnreadCount();
      return () => {
        wsClient.disconnect();
      };
    }, [myPlatformUserId, wsClient, refreshChatUnreadCount]);

    return <>{children}</>;
  },
);
